import { useMedia } from "@next-platform/client-common-spicy-ui";
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from "react";

import useCharacterCardGridConfig from "@/hooks/useCharacterCardGridConfig";
import computeDiscoverSkeletonCount from "@/pages/Discover/computeDiscoverSkeletonCount";

const useDiscoverRowLayout = () => {
  const { isMobile } = useMedia();
  const { colWidth, colGap } = useCharacterCardGridConfig();
  const [stateTrackElement, setStateTrackElement] = useState<HTMLDivElement | null>(null);
  const [stateContainerWidth, setStateContainerWidth] = useState(0);

  const handleTrackRef = useCallback((trackElement: HTMLDivElement | null) => {
    setStateTrackElement((currentElement) => (currentElement === trackElement ? currentElement : trackElement));
  }, []);

  const updateContainerWidth = useCallback((trackElement: HTMLDivElement | null) => {
    if (trackElement === null) {
      return;
    }
    setStateContainerWidth(trackElement.clientWidth);
  }, []);

  useEffect(() => {
    updateContainerWidth(stateTrackElement);
    if (stateTrackElement === null || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateContainerWidth(stateTrackElement);
    });
    resizeObserver.observe(stateTrackElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, [stateTrackElement, updateContainerWidth]);

  const skeletonCount = computeDiscoverSkeletonCount({
    containerWidth: stateContainerWidth,
    colWidth,
    colGap,
    isMobile,
  });

  const trackStyle = useMemo(
    (): CSSProperties => ({
      gridAutoColumns: `${colWidth}px`,
      gap: colGap,
    }),
    [colWidth, colGap],
  );

  return { refTrack: handleTrackRef, trackElement: stateTrackElement, trackStyle, isMobile, skeletonCount };
};

export default useDiscoverRowLayout;
